import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { ColorMode } from '@/types/theme'

const STORAGE_KEY = 'RCH_COLOR_MODE'
const MEDIA_QUERY = '(prefers-color-scheme: dark)'

function parseMode(value: unknown): ColorMode {
  if (value === 'light' || value === 'dark' || value === 'system') return value
  return 'light'
}

function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false
  if (!window.matchMedia) return false
  return window.matchMedia(MEDIA_QUERY).matches
}

function applyToDom(isDark: boolean) {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  root.classList.toggle('dark', isDark)
  root.style.colorScheme = isDark ? 'dark' : 'light'
}

export const useThemeStore = defineStore('theme', () => {
  const mode = ref<ColorMode>('light')
  const systemPrefersDark = ref(false)

  let mql: MediaQueryList | null = null
  let mqlListener: ((event: MediaQueryListEvent) => void) | null = null

  const isDark = computed(() => {
    return mode.value === 'dark' || (mode.value === 'system' && systemPrefersDark.value)
  })

  function detachMediaListener() {
    if (!mql || !mqlListener) return

    // Prefer modern API; fallback for older Safari.
    if (mql.removeEventListener) {
      mql.removeEventListener('change', mqlListener)
    } else {
      mql.removeListener(mqlListener)
    }

    mqlListener = null
    mql = null
  }

  function attachMediaListener() {
    if (typeof window === 'undefined') return
    if (!window.matchMedia) return

    if (!mql) {
      mql = window.matchMedia(MEDIA_QUERY)
    }

    if (mqlListener) return

    mqlListener = event => {
      systemPrefersDark.value = event.matches
      applyToDom(isDark.value)
    }

    // Prefer modern API; fallback for older Safari.
    if (mql.addEventListener) {
      mql.addEventListener('change', mqlListener)
    } else {
      mql.addListener(mqlListener)
    }
  }

  function syncMediaListener() {
    if (mode.value === 'system') {
      attachMediaListener()
      return
    }

    detachMediaListener()
  }

  function setMode(nextMode: ColorMode) {
    mode.value = nextMode

    if (typeof window !== 'undefined') {
      window.localStorage?.setItem(STORAGE_KEY, nextMode)
    }

    systemPrefersDark.value = getSystemPrefersDark()
    syncMediaListener()
    applyToDom(isDark.value)
  }

  function toggle() {
    setMode(isDark.value ? 'light' : 'dark')
  }

  function init() {
    if (typeof window === 'undefined') return

    mode.value = parseMode(window.localStorage?.getItem(STORAGE_KEY))
    systemPrefersDark.value = getSystemPrefersDark()
    syncMediaListener()
    applyToDom(isDark.value)
  }

  return {
    mode,
    isDark,
    init,
    setMode,
    toggle,
  }
})

